import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import reportJobService, { mergeJob } from '../api/v1/reportJobService'
import { ACTIVE_STATES, deriveState, POLL_MS, QUEUE_PAGE_SIZE } from '../config/queueConfig'

const READ_KEY = 'cb:reportJobsRead'
/** Spec 176 — a poll tick older than this no longer holds back the next one (axios times out at 30s). */
const TICK_STALE_MS = 35_000

/** Job ids the user has already seen, persisted so a reload doesn't re-flag everything. */
function loadRead() {
  try {
    const raw = localStorage.getItem(READ_KEY)
    return new Set(raw ? JSON.parse(raw) : [])
  } catch { return new Set() }
}
function saveRead(ids) {
  // Cap the stored set — jobs are TTL'd, so an unbounded list would grow forever.
  try { localStorage.setItem(READ_KEY, JSON.stringify([...ids].slice(-200))) } catch { /* private mode */ }
}

/**
 * Backs the notification center and the Queue page (spec 055/056). Lists the
 * user's report jobs (durable in D1, so they survive a log-out) and keeps
 * in-flight ones advancing — each status poll advances the job server-side, so a
 * user who logs back in later sees finished reports and resumes running ones.
 *
 * Polls even when the tab is hidden so a report still completes (and can notify)
 * while the user is on another tab; browsers throttle hidden-tab timers to ~60s,
 * which is fine for advancing a job one step.
 *
 * Jobs come back NORMALIZED (see reportJobService.normalizeJob) — consumers read
 * camelCase only and never deal with the snake/camel split between the two
 * endpoints.
 */
export function useMyReports({ pollMs = POLL_MS, enabled = true, scope = 'mine', pageSize = QUEUE_PAGE_SIZE } = {}) {
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(false)
  // CC-53047: null while the last read succeeded. Callers MUST gate their empty state
  // on this — an empty list with an error is a failed read, not an empty queue.
  const [error, setError] = useState(null)
  // CC-53049: the server's own totals, so tab counts stop being computed over whatever
  // slice happened to be fetched. `null` means the server did not send them (an older
  // worker), and the caller falls back to counting the page — degraded but not wrong.
  const [total, setTotal] = useState(null)
  const [stateCounts, setStateCounts] = useState(null)
  // Spec 152 review — when the server classified `stateCounts` (null on an older worker,
  // which does not count an expired result separately from a ready one).
  const [countedAt, setCountedAt] = useState(null)
  // Spec 152 review — the results bucket's age as the list ENVELOPE declares it: null
  // until a read has answered or when the worker declares none, and either way the page
  // states no retention period. Read from the envelope, not the rows: an empty queue has
  // no row to read it from, and every first paint has none yet.
  const [resultMaxAgeDays, setResultMaxAgeDays] = useState(null)
  const [readIds, setReadIds] = useState(loadRead)
  // CC-50150: false when the server couldn't confirm the caller's group audience
  // and fell back to 'mine'. Surfaced so the page can say so — a silently
  // narrowed list is indistinguishable from "your group has no activity".
  const [scopeResolved, setScopeResolved] = useState(true)
  // When the list currently on screen was actually READ from the server (epoch ms).
  //
  // This exists so a consumer can anchor a time window to the DATA rather than to a
  // render-time clock — see the `jobs` memo in ReportQueue.jsx. It is deliberately
  // NOT a render-time value and NOT a ticker: it moves when, and only when, a read
  // succeeds and new rows are committed.
  //
  // `null` until the first successful read. At that point `jobs` is still `[]`, so a
  // consumer has nothing to filter yet and the null is never a hidden default. A
  // FAILED poll leaves it alone on purpose: the rows on screen are still the older
  // list, so their fetch time is still the older one.
  const [fetchedAt, setFetchedAt] = useState(null)
  const timer = useRef(null)

  const refresh = useCallback(async () => {
    if (!enabled) return
    try {
      const page = await reportJobService.list({ scope, limit: pageSize })
      // Stamped from the moment the LIST resolved, not from the end of the status
      // fan-out below and not at render: this is the age of the data, and the only
      // clock read in this hook that a consumer's filtering depends on.
      const readAt = Date.now()
      const list = page.jobs
      // A successful read clears any previous error. Done here rather than before the
      // await so a failing poll cannot flicker the error away and back every 5 seconds.
      setError(null)
      setTotal(page.total ?? null)
      setStateCounts(page.stateCounts ?? null)
      setCountedAt(page.countedAt ?? null)
      setResultMaxAgeDays(page.resultMaxAgeDays ?? null)
      // CC-50231 TC5b — the degradation warning used to ERASE ITSELF.
      //
      // The banner on the Queue page has existed all along, so QA's "the client
      // silently swallows the flag" is not right as stated — but their observation of
      // never seeing it is. This line was an unconditional `setScopeResolved(...)`, and
      // `refresh` runs every 5 seconds. The group lookup degrades on roughly the first
      // call against a cold CB cache (one serial call per group; QA timed one at 17s),
      // so the sequence was: first call returns `false` → banner appears → the next
      // poll succeeds 5s later → banner cleared. A warning that deletes itself before
      // the request it describes has even returned is, in practice, invisible.
      //
      // So a `false` is STICKY for as long as the user stays on this scope: the list
      // they are looking at really was narrowed, and that stays true whether or not a
      // later poll would have succeeded. It resets when `scope` changes — `refresh` is
      // rebuilt then, and the effect below re-runs.
      //
      // Also honour the server's SECOND tell, which was being thrown away entirely: on
      // a fallback it echoes `scope: 'mine'` while we asked for something else. Reading
      // both means one of them going missing cannot make the degradation silent again.
      const echoedFallback = page.scope && page.scope !== scope
      if (page.scopeResolved === false || echoedFallback) setScopeResolved(false)
      // Advance running jobs (server-side advance-on-poll) so they progress even
      // when the user isn't on the report page, then merge the fresh status in.
      // mergeJob (not object spread) because the status endpoint omits
      // expiresAt/actionKey — a spread of the normalized status would null them.
      const merged = await Promise.all(list.map(async (j) => {
        if (!ACTIVE_STATES.has(j.state)) return j
        // Spec 169 *review* — only the reader's OWN run is status-polled. The status read
        // advances a job only for its owner; for anyone else's row it never did anything
        // (a 404 before spec 169), and since spec 169 an administrator's read of another
        // user's private-schedule run is a READ-ONLY detail that asks CB Get_IsUserAdmin
        // every time — once per such running row, every 5 seconds, for nothing: this list
        // poll already carries the row's state. Strictly `=== false`, the server's own
        // answer: a row from an older worker without the flag is still polled. This caches
        // no security decision; the drawer's detail read and every result read still ask.
        if (j.isOwner === false) return j
        const st = await reportJobService.status(j.id).catch(() => null)
        return st ? mergeJob(j, st) : j
      }))
      setJobs(merged)
      setFetchedAt(readAt)
    } catch (err) {
      // CC-53047 — this was a bare `catch { /* keep the last known list */ }`.
      //
      // On the FIRST load `jobs` is still its initial `[]`, so a 503, 504, 403,
      // malformed body or aborted request rendered byte-identically to a genuinely
      // empty queue: same empty state, same "0" counts, no error anywhere. The hook
      // exposed no `error` at all, so no caller could have told the difference even if
      // it wanted to. "Keep the last known list" was only ever true on a LATER poll.
      //
      // Now the failure is surfaced. The last known list is still kept — a transient
      // poll failure must not blank a queue the user is reading — but the error rides
      // alongside it, so the page can say "this is stale, and here is why".
      setError(err)
    }
  }, [enabled, scope, pageSize])

  useEffect(() => {
    if (!enabled) return undefined
    setLoading(true)
    /*
     * Spec 176 (CC-54206) — a tick is SKIPPED while the previous one is still out.
     *
     * This was `setInterval(refresh, pollMs)`, which starts a new list + status fan-out
     * every 5s whether or not the last one has answered. Measured on QA 2026-10-13
     * (report 121): each status read was held 17–24s, so every mounted copy of this hook
     * (the Queue badge and the bell are two) kept three or four reads of the same running
     * job open at once, and each read advances the job on the server. The skip is bounded
     * by `TICK_STALE_MS` so a read the client parks forever (the 401 re-login path)
     * cannot stop polling for good.
     */
    let tickStartedAt = Date.now()
    const tick = () => {
      if (tickStartedAt != null && Date.now() - tickStartedAt < TICK_STALE_MS) return
      tickStartedAt = Date.now()
      refresh().finally(() => { tickStartedAt = null })
    }
    refresh().finally(() => { setLoading(false); tickStartedAt = null })
    timer.current = setInterval(tick, pollMs)
    return () => clearInterval(timer.current)
  }, [enabled, pollMs, refresh])

  const dismiss = useCallback(async (id) => {
    setJobs((js) => js.filter((j) => j.id !== id))
    await reportJobService.remove(id).catch(() => null)
  }, [])

  /**
   * Clear the whole list.
   *
   * There is no bulk-delete endpoint, so this issues one DELETE per job. That is
   * deliberate and NOT silently papered over — a `DELETE /reports/jobs` bulk route
   * is a cb-api ask, flagged rather than built here. With the list capped at 25
   * rows the fan-out is bounded.
   *
   * Like single dismiss, this only removes ROWS: an in-flight run keeps running on
   * the server (real cancel is CC-50150). Callers must say so before confirming.
   */
  const dismissAll = useCallback(async (ids) => {
    const targets = ids ?? jobs.map((j) => j.id)
    if (!targets.length) return
    const drop = new Set(targets)
    setJobs((js) => js.filter((j) => !drop.has(j.id)))
    await Promise.all(targets.map((id) => reportJobService.remove(id).catch(() => null)))
  }, [jobs])

  const markRead = useCallback((id) => {
    setReadIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev); next.add(id); saveRead(next); return next
    })
  }, [])

  const markAllRead = useCallback(() => {
    setReadIds(() => {
      const next = new Set(jobs.map((j) => j.id)); saveRead(next); return next
    })
  }, [jobs])

  // A clock kept in state rather than read during render — calling Date.now()
  // inside useMemo is impure and makes the memo update unpredictably.
  // 30s granularity is plenty for deciding "has this result expired"; surfaces
  // that need a per-second clock (elapsed time) keep their own ticker.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    // setState lives only in the interval callback — an external subscription,
    // not a synchronous effect-body write.
    const t = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(t)
  }, [])

  // Effective states, with expiry derived client-side.
  const withState = useMemo(
    () => jobs.map((j) => ({ ...j, effectiveState: deriveState(j, now), read: readIds.has(j.id) })),
    [jobs, readIds, now]
  )

  const runningCount = withState.filter((j) => ACTIVE_STATES.has(j.effectiveState)).length
  const readyCount = withState.filter((j) => j.effectiveState === 'ready').length
  const unreadCount = withState.filter((j) => !j.read && j.effectiveState === 'ready').length

  return {
    jobs: withState,
    loading,
    refresh,
    dismiss,
    dismissAll,
    markRead,
    markAllRead,
    runningCount,
    readyCount,
    unreadCount,
    scopeResolved,
    // CC-53047: null when the last read succeeded. A caller that renders an empty state
    // without checking this is reproducing the bug.
    error,
    // CC-53049: the server's own totals for the current scope. `total` is every run;
    // `stateCounts` is { ready: n, failed: n, … } across ALL of them, not just the page.
    // Both null on an older worker — the caller must then fall back to counting the
    // page and should say the counts are page-local rather than pretend otherwise.
    total,
    stateCounts,
    // Spec 152 review: see the two states above.
    countedAt,
    resultMaxAgeDays,
    // When the rows above were read from the server (epoch ms), or null before the
    // first successful read. Anchor any "last N days" window to THIS, never to a
    // render-time `Date.now()` — see ReportQueue.jsx.
    fetchedAt,
  }
}

export default useMyReports
